import React from 'react';
import PropTypes from 'prop-types';
import { YButton } from './YButton';

/**
 * Shared prev/next pagination control (club board, hunt logs, tables).
 * Renders nothing when count <= limit.
 */
export function YPagination({
  count,
  offset,
  limit,
  onPrev,
  onNext,
  showPageCount = false,
  prevLabel = 'Previous',
  nextLabel = 'Next',
  ariaLabel = 'Pagination',
  className = '',
  dataTestId = 'y-pagination',
}) {
  const safeLimit = Math.max(1, limit || 1);
  const safeCount = Math.max(0, count || 0);

  if (safeCount <= safeLimit) {
    return null;
  }

  const currentPage = Math.floor(offset / safeLimit) + 1;
  const totalPages = Math.ceil(safeCount / safeLimit);
  const startIndex = safeCount === 0 ? 0 : offset + 1;
  const endIndex = Math.min(offset + safeLimit, safeCount);

  const metaText = showPageCount
    ? `Page ${currentPage} of ${totalPages} · ${startIndex}–${endIndex} of ${safeCount}`
    : `${startIndex}–${endIndex} of ${safeCount}`;

  return (
    <nav
      className={`y-pagination ${className}`.trim()}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      <YButton
        type="button"
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          onPrev?.();
        }}
        disabled={offset <= 0}
      >
        {prevLabel}
      </YButton>
      <span className="y-pagination__meta">{metaText}</span>
      <YButton
        type="button"
        variant="secondary"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          onNext?.();
        }}
        disabled={offset + safeLimit >= safeCount}
      >
        {nextLabel}
      </YButton>
    </nav>
  );
}

YPagination.propTypes = {
  count: PropTypes.number.isRequired,
  offset: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  showPageCount: PropTypes.bool,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
};
